import type React from 'react';
import { useState } from 'react';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DocumentExpiryCategory } from './DocumentExpiryCategory';
import { ReminderToViewCategory } from './ReminderToViewCategory';
import { ReminderToSignCategory } from './ReminderToSignCategory';
import { ThankYouEmailCategory } from './ThankYouEmailCategory';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { useCurrentUserPlanApiData } from 'hooks/useCurrentUserPlanApiData';
import { UpgradePlanCard } from './UpgradePlanCard';
import { supportUrls } from 'utils/urls';

interface AutomaricReminderTabProps {
  onBack: VoidFunction;
}

type ReminderState = 'expiry' | 'view' | 'sign' | 'thank_you_email';

const reminderCategories: { title: string; description: string; state: ReminderState }[] = [
  {
    title: 'share_document_v3.automatic_reminders.document_expiry',
    description: 'share_document_v3.automatic_reminders.document_expiry_description',
    state: 'expiry',
  },
  {
    title: 'share_document_v3.automatic_reminders.reminder_to_view',
    description: 'share_document_v3.automatic_reminders.reminder_to_view_description',
    state: 'view',
  },
  {
    title: 'share_document_v3.automatic_reminders.reminder_to_sign',
    description: 'share_document_v3.automatic_reminders.reminder_to_sign_description',
    state: 'sign',
  },
  {
    title: 'share_document_v3.automatic_reminders.thank_you_email',
    description: 'share_document_v3.automatic_reminders.thank_you_email_description',
    state: 'thank_you_email',
  },
];

const AutomaticReminderTitle = ({ onBack }: { onBack: VoidFunction }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.5} direction="row" alignItems="flex-start">
      <IconButton onClick={onBack}>
        <ChevronLeft />
      </IconButton>
      <Stack direction="column" spacing={1}>
        <Typography variant="h5" fontSize="24px" fontWeight={600} color="text.primary">
          {t('share_document_v3.automatic_reminders.title')}
        </Typography>
        <Typography variant="body1" fontSize="16px" fontWeight={400} color="text.secondary">
          <Trans
            i18nKey="share_document_v3.automatic_reminders.description"
            components={{
              a: <a href={supportUrls.get(supportUrls.automaticReminder)} target="_blank" rel="noopener" />,
            }}
          />
        </Typography>
      </Stack>
    </Stack>
  );
};

export const AutomaticReminderTab: React.FC<AutomaricReminderTabProps> = ({ onBack }) => {
  const { [FeatureFlags.autoReminders]: isAutoRemindersEnabled } = useFeatureFlag([FeatureFlags.autoReminders]);
  const [categories, setCategory] = useState<Set<ReminderState>>(new Set());
  const { t } = useTranslation();
  const { data: planData, isLoading: isPlanLoading } = useCurrentUserPlanApiData();
  const hasAutomaticRemindersFeature = planData?.tier === 'team' || planData?.tier === 'business' || planData?.tier === 'employee';

  const handleOnChangeCategory = (category: ReminderState) => {
    setCategory((prev) => {
      const newSet = new Set(prev);
      if (prev.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  if (!isAutoRemindersEnabled) {
    return null;
  }

  if (isPlanLoading) {
    return (
      <CardContent sx={{ width: '344px' }} data-testid="automatic-reminders-loading-tab">
        <AutomaticReminderTitle onBack={onBack} />
        <Stack spacing={2} pt={2}>
          <Skeleton width="100%" height="80px" variant="rounded" animation="wave" />
          <Skeleton width="100%" height="24px" variant="rounded" animation="wave" />
          <Skeleton width="100%" height="24px" variant="rounded" animation="wave" />
        </Stack>
      </CardContent>
    );
  }

  if (!hasAutomaticRemindersFeature) {
    return <UpgradePlanCard title={<AutomaticReminderTitle onBack={onBack} />} />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CardContent sx={{ maxWidth: '344px' }} data-testid="automatic-reminders-tab">
        <AutomaticReminderTitle onBack={onBack} />
        <Stack>
          {reminderCategories.map((category, index) => (
            <>
              <Stack key={index} pt="12px" pb={index !== reminderCategories.length - 1 ? '12px' : 0} spacing={2}>
                <FormControlLabel
                  value="bottom"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                  control={<Switch color="primary" onChange={() => handleOnChangeCategory(category.state)} />}
                  label={
                    <Typography
                      variant="body1"
                      fontSize="16px"
                      color="text.primary"
                      fontWeight={categories.has(category.state) ? 600 : 400}
                    >
                      {t(category.title)}
                    </Typography>
                  }
                  labelPlacement="start"
                />
                {category.state === 'expiry' && categories.has(category.state) && <DocumentExpiryCategory />}
                {category.state === 'view' && categories.has(category.state) && <ReminderToViewCategory />}
                {category.state === 'sign' && categories.has(category.state) && <ReminderToSignCategory />}
                {category.state === 'thank_you_email' && categories.has(category.state) && <ThankYouEmailCategory />}
              </Stack>
              {index !== reminderCategories.length - 1 && <Divider />}
            </>
          ))}
        </Stack>
      </CardContent>
    </LocalizationProvider>
  );
};
